import React from "react";
import { useState, useEffect } from "react";

import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import PropTypes from "prop-types";
import L from "leaflet"; // Ensure L is imported
import { getAreaPerformance } from "services/api";
import { useTranslation } from "react-i18next";
import { CircularProgress, Box } from "@mui/material";

const getIconByPerformance = (performance) => {
  let iconUrl;

  if (performance == -1 || !performance) {
    iconUrl =
      "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-grey.png"; // ⚫ No Data
  } else if (performance >= 105) {
    iconUrl =
      "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png"; // 🟢 Excellent Performance
  } else if (performance >= 90 && performance < 105) {
    iconUrl =
      "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png"; // 🔵 Good Performance
  } else if (performance >= 80 && performance < 90) {
    iconUrl =
      "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png"; // 🔵 Good Performance
  } else if (performance >= 70 && performance < 80) {
    iconUrl =
      "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-yellow.png"; // 🟡 Moderate Performance
  } else {
    iconUrl =
      "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png"; // 🔴 Very Low Performance
  }

  return new L.Icon({
    iconUrl,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    shadowSize: [41, 41],
  });
};

const getStatusColor = (performance) => {
  if (performance == -1)
    return "grey"; // Excellent
  else if (performance >= 105)
    return "blue"; // Average
  else if (performance >= 90 && performance < 105)
    return "green"; // Average
  else if (performance >= 80 && performance < 90)
    return "orange"; // Average
  else if (performance >= 70 && performance < 80)
    return "yellow"; // Average
  else return "red"; // Poor
};

const EgyptMap2 = ({ areas, year, month, center }) => {
  const { t } = useTranslation();
  const [areaResults, setAreaResults] = useState({});

  useEffect(() => {
    const fetchPerformanceData = async () => {
      // if (branches.length === 0) return;
      if (!areas || areas.length === 0) return; // Ensure branches is defined
      const results = {};
      await Promise.all(
        areas.map(async (area) => {
          const result = await getAreaPerformance(year, month, area.areaId);
          if (result != "error")
            results[area.areaId] = result?.overallAreaAverage?.toFixed(2) ?? "N/A";
        })
      );
      setAreaResults(results);
    };

    fetchPerformanceData();
  }, [month, year, areas]);

  return (
    <MapContainer
      center={center} // Egypt's latitude and longitude
      zoom={6}
      style={{ height: "300px", width: "100%" }}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      />
      {areas?.map((area, index) => (
        <Marker
          key={index}
          position={[area.latitude, area.longitude]}
          // icon={customIcon2}
          icon={getIconByPerformance(areaResults[area.areaId])}
        >
          <Popup>
            <strong>{area.areaName}</strong> <br />
            {area.address}
            {Number(areaResults[area.areaId]) === -1 ? (
              t("user.dashboards.noData")
            ) : areaResults[area.areaId] ? (
              <>
                {areaResults[area.areaId]}%
                <span
                  style={{
                    display: "inline-block",
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    backgroundColor: getStatusColor(areaResults[area.areaId]),
                    marginLeft: "5px",
                  }}
                ></span>
              </>
            ) : (
              // t("user.dashboards.noData")
              <Box display="flex" justifyContent="center" p={1}>
                <CircularProgress size={20} sx={{ color: "black" }} />
              </Box>
            )}
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
};

EgyptMap2.propTypes = {
  areas: PropTypes.arrayOf(
    PropTypes.shape({
      latitude: PropTypes.number.isRequired,
      longitude: PropTypes.number.isRequired,
      areaName: PropTypes.string.isRequired,
      address: PropTypes.string.isRequired,
    })
  ).isRequired,
  year: PropTypes.number.isRequired,
  month: PropTypes.number.isRequired,
  center: PropTypes.number.isRequired,
};

export default EgyptMap2;
